import { useGetApplicationTalentHuntJobQuery } from '@/app/features/recruitment/job-applicants/job-applicants.api';
import AIIcon from '@/assets/icons/AIIcon';
import CustomTable from '@/components/CustomTable';
import PageTitle from '@/components/PageTitle';
import StatusTag from '@/components/StatusTag';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters } from '@/hooks';
import { DownloadIcon, SearchIcon } from '@/icons';
import DashboardLayout from '@/layouts/DashboardLayout';
import { Button, Flex, Input, Space, type TableProps } from 'antd';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import SendEmailModal from './SendEmailModal';

export default function TalentHuntDetails() {
  const { id } = useParams<{ id: string }>();
  const { page, skip, limit, keyword, handleSearch, handlePagination } =
    useAppFilters(FILTER_KEYS.TALENT_HUNT);
  const [selectedData, setSelectedData] = useState<string[]>([]);

  const [config, handleOpen, handleClose] = useAppDiscloser<'SEARCH'>(
    DISCLOSER_KEYS.JOB_APPLICANT,
  );

  const { data: applicantsData, isFetching } =
    useGetApplicationTalentHuntJobQuery(
      { id: id as string, search: keyword, skip, limit },
      { skip: !id },
    );

  // Derive full applicant objects for selected rows
  const selectedApplicants = (applicantsData?.data?.data || []).filter((item) =>
    selectedData.includes(item.id),
  );

  const columns: TableProps<any>['columns'] = [
    {
      title: 'Candidate Name',
      dataIndex: 'candidate_name',
      render: (val: string) => val || 'n/a',
    },
    {
      title: 'Job Title',
      dataIndex: 'job_title',
      render: (val: string) => val || 'n/a',
    },
    {
      width: 100,
      title: 'Source',
      dataIndex: 'sources',
      render: (val: string) => (
        <Link
          to={val}
          target="_blank"
          className="text-theme-primary hover:text-theme-primary underline hover:underline underline-offset-2 truncate w-40 block"
        >
          {val || 'N/A'}
        </Link>
      ),
    },
    {
      title: 'Position',
      dataIndex: 'position',
      render: (val) => val ?? 'n/a',
    },
    {
      title: 'Email address',
      dataIndex: 'email',
      render: (val) => val ?? 'n/a',
    },
    {
      align: 'center',
      title: 'Email Sent',
      dataIndex: 'is_sent_email',
      render: (val) => (
        <StatusTag
          value={val}
          config={[
            {
              label: 'Yes',
              value: true as unknown as string,
              className: 'status status-success',
            },
            {
              label: 'No',
              value: false as unknown as string,
              className: 'status status-error',
            },
          ]}
        />
      ),
    },
    // {
    //   title: 'Actions',
    //   dataIndex: 'id',
    //   align: 'center',
    //   render: (_: string) => (
    //     <Space size={8}>
    //       <Button
    //         size="small"
    //         className="action-btn"
    //         icon={<Trash2Icon size={20} />}
    //       />
    //     </Space>
    //   ),
    // },
  ];

  return (
    <DashboardLayout title="Job & Applicants">
      <PageTitle
        classNames={{ root: 'items-start' }}
        title="Jobs"
        items={[
          { title: 'Dashboard', path: '/admin/recruitment/dashboard' },
          { title: 'TalentHunt', path: '/admin/recruitment/job-applicants' },
          { title: 'TalentHunt Details' },
        ]}
      >
        <Space>
          <Input
            size="large"
            onChange={handleSearch}
            className="shrink-0 w-[225px]"
            placeholder="Search Candidates"
            prefix={<SearchIcon size={24} />}
          />
          {/* <Select
            allowClear
            size="large"
            placeholder="Looking For UX Designers"
            placement="bottomLeft"
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
            // onChange={handleChange}
            options={[]}
          /> */}
          <Button
            disabled
            size="large"
            className="btn-primary-bordered"
            icon={<DownloadIcon size={16} strokeWidth={2.5} />}
            // onClick={() => handleOpen({ type: "IMPORT" })}
          >
            Export
          </Button>
        </Space>
      </PageTitle>
      <br />

      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={applicantsData?.data?.data || []}
        onPaginate={handlePagination}
        customTitle={
          <Flex align="center" justify="space-between">
            <Flex align="center" gap={10}>
              <span>TalentHunt</span>
              <AIIcon />
            </Flex>
            <Button
              type="primary"
              size="large"
              className="btn-primary"
              disabled={!selectedData.length}
              onClick={() => handleOpen({ type: 'VIEW', data: selectedData })}
            >
              Send Email
            </Button>
          </Flex>
        }
        loading={isFetching}
        total={applicantsData?.data?.total || 0}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedData,
          onChange: (key) => {
            setSelectedData(key as string[]);
          },
          getCheckboxProps: (record) => ({
            disabled: record?.is_sent_email,
            className: 'custom-check',
          }),
          columnWidth: 40,
        }}
      />

      <SendEmailModal
        selectedApplicants={selectedApplicants}
        type={config.type}
        handleClose={handleClose}
        open={config.open && config.type === 'VIEW'}
      />
    </DashboardLayout>
  );
}
